import { loadFavouriteEventIds } from "../../helpers/localStorageHelper";
import { syncFavouritesToDB } from "../../helpers/favouritesSyncHelper.js";
import { getCurrentDate, getLastDayOfMonth } from "../../helpers/dateHelper";
import {
  fetchEventsData,
  fetchEventDatesData,
  fetchFavouriteEventsData,
  buildElasticFilters,
  getPredefinedVenue,
} from "../../apis/elasticApi";

// Get the current URL parameters
const urlParams = new URLSearchParams(window.location.search);
const __khstCalendarFilterSearchTerm__ = urlParams.get("searchTerm") || "";
const __khstCalendarFilterDate__ = urlParams.get("date") || getCurrentDate();
const __khstCalendarFilterSelectedFilters = {
  drupalCategory: [],
  tixVenue: [],
  tixHall: [],
  drupalGrade: [],
  tixCapacity: [],
  drupalTema: [],
};
if (window.__khstCalendarFilterPredefinedCategory) {
  __khstCalendarFilterSelectedFilters.drupalCategory = window.__khstCalendarFilterPredefinedCategory;
}
if (getPredefinedVenue()) {
  __khstCalendarFilterSelectedFilters.tixVenue = getPredefinedVenue();
}
if (window.__khstCalendarFilterPredefinedHall) {
  __khstCalendarFilterSelectedFilters.tixHall = window.__khstCalendarFilterPredefinedHall;
}
if (window.__khstCalendarFilterPredefinedTema) {
  __khstCalendarFilterSelectedFilters.drupalTema = window.__khstCalendarFilterPredefinedTema;
}
urlParams.forEach((value, key) => {
  if (key.startsWith("drupal") || key.startsWith("tix")) {
    __khstCalendarFilterSelectedFilters[key] = value.split(",");
  }
});

const calendarFilter = {
  state: {
    events: [],
    eventDates: [],
    filters: [],
    searchTerm: __khstCalendarFilterSearchTerm__,
    selectedDate: __khstCalendarFilterDate__, // Default to today'st date
    selectedFilters: __khstCalendarFilterSelectedFilters,
    selectedCustomerType: window.__khstCalendarFilterSelectedCustomerType || "",
    numberOfHits: 0,
    hitsPerPage: 10,
    currentPage: 1,
    loading: false,
    isCalendarOpen: window.__khstCalendarFilterCalendarExpanded,
    isFilterOpen: window.__khstCalendarFilterFilterExpanded,
    calendarStartDate: getCurrentDate(),
    calendarEndDate: getLastDayOfMonth(),
    favouriteEventIds: loadFavouriteEventIds(),
    favouriteEventLists: [],
  },
  getters: {
    getEvents: (state) => state.events,
    getEventDates: (state) => {
      return state.eventDates.map((event) => {
        return event._source.tixStartDate;
      });
    },
    getFilters: (state) => state.filters,
    getSearchTerm: (state) => state.searchTerm,
    getSelectedDate: (state) => state.selectedDate,
    getSelectedFilters: (state) => state.selectedFilters,
    getNumberOfHits: (state) => state.numberOfHits,
    getLoading: (state) => state.loading,
    getCurrentNoOfHits: (state) =>
      state.hitsPerPage * state.currentPage <= state.numberOfHits
        ? state.hitsPerPage * state.currentPage
        : state.numberOfHits,
    hasMoreEvents: (state) => state.events.length < state.numberOfHits,
    isSchool: (state) => {
      return window.__khstCalendarFilterCustomerTypeName === "Skola";
    },
    getIsCalendarOpen: (state) => state.isCalendarOpen,
    getIsFilterOpen: (state) => state.isFilterOpen,
    hasFavourites: (state) => state.favouriteEventIds.length > 0,
    totalFavourites: (state) => state.favouriteEventIds.length,
    favouriteEventIds: (state) => state.favouriteEventIds,
    favouriteEventLists: (state) => state.favouriteEventLists,
    hasMoreFavourites: (state) =>
      state.favouriteEventLists.length < state.numberOfHits,
    hasActiveFilters: (state) => {
      return (
        state.selectedFilters.drupalCategory.length > 0 ||
        state.selectedFilters.drupalTema.length > 0 ||
        state.selectedFilters.tixVenue.length > 0 ||
        state.selectedFilters.tixHall.length > 0 ||
        state.selectedFilters.drupalGrade.length > 0 ||
        state.selectedFilters.tixCapacity.length > 0 ||
        state.searchTerm.length > 0 ||
        state.selectedDate !== getCurrentDate()
      );
    },
  },
  mutations: {
    SET_EVENTS(state, value) {
      state.currentPage = 1;
      state.events = value;
    },
    SET_EVENT_DATES(state, value) {
      state.eventDates = value;
    },
    SET_EVENTS_LOAD_MORE(state, value) {
      state.currentPage++;
      state.events = [...state.events, ...value];
    },
    SET_FILTERS(state, value) {
      state.filters = value;
    },
    SET_SEARCH_TERM(state, value) {
      state.searchTerm = value;
    },
    SET_SELECTED_DATE(state, value) {
      state.selectedDate = value;
    },
    SET_SELECTED_FILTERS(state, value) {
      state.selectedFilters = value;
    },
    SET_NUMBER_OF_HITS(state, value) {
      state.numberOfHits = value;
    },
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_CALENDAR_START_DATE(state, value) {
      state.calendarStartDate = value;
    },
    SET_CALENDAR_END_DATE(state, value) {
      state.calendarEndDate = value;
    },
    SET_IS_CALENDAR_OPEN(state, value) {
      state.isCalendarOpen = value;
    },
    SET_IS_FILTER_OPEN(state, value) {
      state.isFilterOpen = value;
    },
    SET_FAVOURITE_EVENT_IDS(state, value) {
      state.favouriteEventIds = [...value];
    },
    ADD_FAVOURITE_EVENT_ID(state, value) {
      if (!state.favouriteEventIds.includes(value)) {
        state.favouriteEventIds = [...state.favouriteEventIds, value];
      }
    },
    REMOVE_FAVOURITE_EVENT_ID(state, value) {
      state.favouriteEventIds = state.favouriteEventIds.filter(
        (i) => i !== value
      );
    },
    SET_FAVOURITE_EVENT_LISTS(state, value) {
      state.favouriteEventLists = [...value];
    },
    SET_FAVOURITE_LOAD_MORE(state, value) {
      state.currentPage++;
      state.favouriteEventLists = [...state.favouriteEventLists, ...value];
    },
  },
  actions: {
    setCurrentMonth({ state, commit, dispatch }, payload) {
      commit("SET_CALENDAR_START_DATE", payload.startDate);
      commit("SET_CALENDAR_END_DATE", payload.endDate);

      dispatch("fetchCalendarDates");
    },
    fetchCalendarDates({ state, commit }) {
      fetchEventDatesData(
        state.calendarStartDate,
        state.calendarEndDate,
        state.searchTerm,
        state.selectedCustomerType,
        buildElasticFilters(state.selectedFilters)
      )
        .then((data) => {
          commit("SET_EVENT_DATES", data.hits.hits);
        })
        .catch((error) => {
          console.error("Failed to fetch data:", error);
        });
    },
    fetchCalendarEvents({ state, commit, dispatch }) {
      commit("SET_LOADING", true);
      dispatch("fetchCalendarDates");

      fetchEventsData(
        state.selectedDate,
        state.searchTerm,
        state.selectedCustomerType,
        buildElasticFilters(state.selectedFilters)
      )
        .then((data) => {
          commit("SET_EVENTS", data.hits.hits);
          commit("SET_FILTERS", data.aggregations);
          commit("SET_NUMBER_OF_HITS", data.hits.total.value);
          commit("SET_LOADING", false);
        })
        .catch((error) => {
          console.error("Failed to fetch data:", error);
        });
    },
    fetchCalendarEventsLoadMore({ state, commit }) {
      commit("SET_LOADING", true);
      fetchEventsData(
        state.selectedDate,
        state.searchTerm,
        state.selectedCustomerType,
        buildElasticFilters(state.selectedFilters),
        state.currentPage * state.hitsPerPage
      )
        .then((data) => {
          commit("SET_EVENTS_LOAD_MORE", data.hits.hits);
          commit("SET_LOADING", false);
        })
        .catch((error) => {
          console.error("Failed to fetch data:", error);
        });
    },
    fetchFavouriteEvents({ state, commit, dispatch }) {
      commit("SET_LOADING", true);

      const storedIds = [...state.favouriteEventIds];
      const fetchSize = Math.max(10, storedIds.length);

      fetchFavouriteEventsData(storedIds, 0, fetchSize)
        .then((data) => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isLoggedIn = localStorage.getItem('loggedInFavouriteCheck') === 'true';
          const returnedIds = new Set(data.hits.hits.map(hit => hit._source.tixEventId.toString()));
          const activeHits = [];

          // Remove events that are past their start date
          data.hits.hits.forEach(hit => {
            const eventDate = new Date(hit._source.tixStartDate);
            eventDate.setHours(0, 0, 0, 0);
            if (eventDate < today) {
              const id = hit._source.tixEventId.toString();
              localStorage.removeItem(`favouriteEventId-${id}`);
              commit('REMOVE_FAVOURITE_EVENT_ID', id);
              if (isLoggedIn) {
                dispatch('removeFavouriteFromDb', id);
              }
            } else {
              activeHits.push(hit);
            }
          });

          // Remove stored IDs that no longer exist in Elasticsearch
          storedIds.forEach(id => {
            if (!returnedIds.has(id)) {
              localStorage.removeItem(`favouriteEventId-${id}`);
              commit('REMOVE_FAVOURITE_EVENT_ID', id);
              if (isLoggedIn) {
                dispatch('removeFavouriteFromDb', id);
              }
            }
          });

          commit("SET_FAVOURITE_EVENT_LISTS", activeHits);
          commit("SET_NUMBER_OF_HITS", data.hits.total.value);
          commit("SET_LOADING", false);
        })
        .catch((error) => {
          console.error("Failed to fetch data:", error);
        });
    },
    fetchFavouriteEventsLoadMore({ state, commit }) {
      commit("SET_LOADING", true);

      fetchFavouriteEventsData(
        state.favouriteEventIds,
        state.currentPage * state.hitsPerPage
      )
        .then((data) => {
          commit("SET_FAVOURITE_LOAD_MORE", data.hits.hits);
          commit("SET_LOADING", false);
        })
        .catch((error) => {
          console.error("Failed to fetch data:", error);
        });
    },
    setSelectedFilters({ state }) {
      const selectedFilters = {};
      Object.entries(state.selectedFilters).map(([key, values]) => {
        if (values.length === 0) {
          return null;
        }
        selectedFilters[key] = values.map((v) => `${v}`).join(",");
      });

      const url = new URL(location);
      Object.entries(state.selectedFilters).map(([key, value]) => {
        url.searchParams.delete(key);
        if (value.length === 0) {
          return null;
        }
        url.searchParams.set(key, value);
      });

      window.history.pushState(null, "", url);
    },
    setSelectedDate({ commit }, value) {
      commit("SET_SELECTED_DATE", value);

      const url = new URL(location);

      if (value === getCurrentDate()) {
        url.searchParams.delete("date");
      } else {
        url.searchParams.set("date", value);
      }

      window.history.pushState(null, "", url);
    },
    setSearchTerm({ state }) {
      const url = new URL(location);

      if (state.searchTerm) {
        url.searchParams.set("searchTerm", state.searchTerm);
      } else {
        url.searchParams.delete("searchTerm");
      }

      window.history.pushState(null, "", url);
    },
    setIsCalendarOpen({ commit }, value) {
      commit("SET_IS_CALENDAR_OPEN", value);
    },
    setIsFilterOpen({ commit }, value) {
      commit("SET_IS_FILTER_OPEN", value);
    },
    clearFilters({ commit, dispatch }) {
      commit("SET_SELECTED_FILTERS", {
        drupalCategory: [],
        tixVenue: [],
        tixHall: [],
        drupalGrade: [],
        tixCapacity: [],
        drupalTema: [],
      });
      commit("SET_SEARCH_TERM", "");

      dispatch("setSelectedDate", getCurrentDate());
      dispatch("setSearchTerm");
      dispatch("setSelectedFilters");
      dispatch("fetchCalendarEvents");
    },
    updateFavouriteEventIds({ commit }) {
      const ids = Object.keys(localStorage)
        .filter((key) => key.startsWith("favouriteEventId-"))
        .map((key) => key.replace("favouriteEventId-", ""));

      commit("SET_FAVOURITE_EVENT_IDS", ids);
    },
    fetchTixlyUid() {
      const drupalUser = window.khstGetUser();
      if (drupalUser && drupalUser.tixly_uid) {
        return Promise.resolve(drupalUser.tixly_uid.toString());
      }
      return Promise.resolve(null);
    },
    fetchFavouriteIdsFromDB({ commit, dispatch }) {
      return fetch('/api/favourites', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status === 'success' && data.event_ids.length > 0) {
            commit('SET_FAVOURITE_EVENT_IDS', data.event_ids);
            dispatch('fetchFavouriteEvents');
            data.event_ids.forEach(id => {
              const key = `favouriteEventId-${id}`;
              if (localStorage.getItem(key)) {
                localStorage.removeItem(key);
              }
            });
            return true;
          }
          return false;
        })
        .catch(() => false);
    },
    syncFavouritesToDB({ dispatch }) {
      return syncFavouritesToDB().then((data) => {
        if (data && data.status === 'success') {
          dispatch('fetchFavouriteIdsFromDB');
        }
        return data;
      });
    },
    addFavouriteToDb({ dispatch }, eventId) {
      const eventIds = [eventId.toString()];
      return fetch('/session/token')
        .then((response) => response.text())
        .then((token) => {
          return fetch('/api/favourites/sync', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              'X-CSRF-Token': token,
            },
            credentials: 'same-origin',
            body: JSON.stringify({ event_ids: eventIds }),
          });
        })
        .then((response) => response.json())
        .then((data) => {
          if (data.status === 'success' && data.saved && data.saved.length > 0) {
            data.saved.forEach(id => {
              localStorage.removeItem(`favouriteEventId-${id}`);
            });
          }
          return data;
        })
        .catch((error) => {
          console.error('Failed to add favourite to DB:', error);
        });
    },
    removeFavouriteFromDb({ dispatch }, eventId) {
      return fetch('/session/token')
        .then((response) => response.text())
        .then((token) => {
          return fetch(`/api/favourites/${eventId}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              'X-CSRF-Token': token,
            },
            credentials: 'same-origin',
            body: JSON.stringify({}),
          });
        })
        .then((response) => response.json())
        .catch((error) => {
          console.error('Failed to remove favourite from DB:', error);
        });
    },

  },
};

export default calendarFilter;
